import { useEffect, useState } from 'react';
import { getProducts } from '../../services/adminService';
import { thumbnail } from '../../lib/imageProxy';

// Mirrors MAX_SALE_ITEMS in api/_manualSaleItems.cjs (the API enforces it)
export const MAX_SALE_ITEMS = 20;
const SEARCH_DEBOUNCE_MS = 300;

const labelStyle = {
  display: 'block', fontSize: '0.82rem', fontWeight: '600', color: '#555',
  marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em',
};
const inputStyle = {
  padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '8px',
  fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
};

const formatMxn = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

/** Sum of the line amounts the admin typed (invalid/empty count as 0). */
export const saleItemsTotal = (items) =>
  Math.round(items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0) * 100) / 100;

/**
 * Products in a direct sale — one row per product with its own price, plus a
 * search to add more products to the same sale (one order, one email).
 *
 * @param {{ id, name, image, amount: string }[]} items
 * @param {(items) => void} onChange
 * @param {string} token   admin access token
 * @param {boolean} disabled
 */
const DirectSaleItemsField = ({ items, onChange, token, disabled = false }) => {
  const [adding, setAdding]     = useState(false);
  const [search, setSearch]     = useState('');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = search.trim();
    if (!adding || !term || !token) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await getProducts(token, { search: term, page: 1, limit: 10 });
        if (!cancelled) setResults(data.products || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, adding, token]);

  const inSale = new Set(items.map((i) => String(i.id)));
  // Each product is a unique piece: sold-out ones and ones already added can't be picked
  const available = results.filter((p) => !p.isSoldOut && !inSale.has(String(p.id)));

  const updateAmount = (id, amount) =>
    onChange(items.map((i) => (i.id === id ? { ...i, amount } : i)));
  const remove = (id) => onChange(items.filter((i) => i.id !== id));
  const add = (product) => {
    onChange([...items, {
      id: product.id,
      name: product.name,
      image: product.image || null,
      amount: product.price != null ? String(product.price) : '',
    }]);
    setSearch('');
    setResults([]);
    setAdding(false);
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={labelStyle}>
        {items.length > 1 ? 'Productos' : 'Producto'} <span style={{ color: '#E4405F' }}>*</span>
      </label>

      {items.map((item) => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0', borderBottom: '1px solid #f3f3f3' }}>
          {item.image
            ? <img src={thumbnail(item.image)} alt="" width="36" height="36" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
            : <div style={{ width: 36, height: 36, borderRadius: 6, background: '#eee', flexShrink: 0 }} />}
          <span style={{ flex: 1, minWidth: 0, fontSize: '0.88rem', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
            {item.name}
          </span>
          <input
            required
            type="number"
            min="1"
            step="0.01"
            aria-label={`Monto de ${item.name} (MXN)`}
            value={item.amount}
            disabled={disabled}
            onChange={(e) => updateAmount(item.id, e.target.value)}
            style={{ ...inputStyle, width: '6.5rem' }}
          />
          <button
            type="button"
            onClick={() => remove(item.id)}
            disabled={disabled || items.length === 1}
            aria-label={`Quitar ${item.name}`}
            title="Quitar"
            style={{
              background: 'none', border: 'none', padding: '0 0.2rem', fontSize: '1.1rem', lineHeight: 1,
              color: items.length === 1 ? '#ddd' : '#dc3545', cursor: items.length === 1 ? 'default' : 'pointer',
            }}
          >
            ×
          </button>
        </div>
      ))}

      {adding ? (
        <div style={{ marginTop: '0.6rem' }}>
          <input
            autoFocus
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') { setAdding(false); setSearch(''); } }}
            placeholder="Buscar por nombre o artista…"
            style={{ ...inputStyle, width: '100%' }}
          />
          {search.trim() && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, marginTop: '0.3rem', maxHeight: 200, overflowY: 'auto' }}>
              {searching && <p style={{ margin: 0, padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#888' }}>Buscando…</p>}
              {!searching && available.length === 0 && (
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#888' }}>Sin productos disponibles.</p>
              )}
              {!searching && available.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => add(p)}
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center', gap: '0.5rem', textAlign: 'left',
                    padding: '0.45rem 0.75rem', background: 'white', border: 'none', borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                  }}
                >
                  <span style={{ flex: 1, fontSize: '0.88rem', color: '#333' }}>{p.name}</span>
                  {p.price != null && <span style={{ fontSize: '0.82rem', color: '#888' }}>{formatMxn(p.price)}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        items.length < MAX_SALE_ITEMS && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={disabled}
            style={{ marginTop: '0.5rem', background: 'none', border: 'none', padding: 0, color: '#2ca9e1', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
          >
            + Agregar producto
          </button>
        )
      )}

      {items.length > 1 && (
        <p style={{ margin: '0.6rem 0 0', textAlign: 'right', fontSize: '0.92rem', color: '#333' }}>
          Total: <strong>{formatMxn(saleItemsTotal(items))}</strong>
        </p>
      )}
    </div>
  );
};

export default DirectSaleItemsField;
